import React, { useState, useEffect } from 'react';
import { payFor } from './shopUtils';

// Little sparkle for the savings badge — matches the app's inline-SVG style.
const SparkIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 2l2.1 6.3a3 3 0 0 0 1.6 1.6L22 12l-6.3 2.1a3 3 0 0 0-1.6 1.6L12 22l-2.1-6.3a3 3 0 0 0-1.6-1.6L2 12l6.3-2.1a3 3 0 0 0 1.6-1.6L12 2z" />
  </svg>
);

// StepAmount — denomination chips plus an optional custom amount (clamped to
// the voucher's min/max). Shows the promo price when the product is discounted.
const StepAmount = ({ voucher, amount, currency, onChange }) => {
  const denoms = (voucher && voucher.denominations) || [];
  const customAllowed = !!(voucher && voucher.customAmount);
  const min = voucher && voucher.customMin != null ? voucher.customMin : 1;
  const max = voucher && voucher.customMax != null ? voucher.customMax : null;

  const amountIsPreset = denoms.includes(amount);
  const [customMode, setCustomMode] = useState(customAllowed && !amountIsPreset);
  const [customText, setCustomText] = useState(amountIsPreset ? '' : String(amount || ''));

  // Drop out of custom mode if the amount becomes a preset (e.g. product change).
  useEffect(() => {
    if (denoms.includes(amount)) setCustomMode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount]);

  const pickPreset = (d) => { setCustomMode(false); onChange(d); };

  const onCustomInput = (raw) => {
    setCustomText(raw);
    const n = parseInt(raw, 10);
    if (Number.isFinite(n)) onChange(n); // live preview; clamp on blur
  };
  const onCustomBlur = () => {
    let n = parseInt(customText, 10);
    if (!Number.isFinite(n)) return;
    if (min != null) n = Math.max(min, n);
    if (max != null) n = Math.min(max, n);
    setCustomText(String(n));
    onChange(n);
  };

  const pay = payFor(voucher, amount);
  const hasPromo = pay < amount;

  return (
    <section className="gcs-step">
      <div className="gcs-chips">
        {denoms.map((d) => (
          <button
            key={d}
            type="button"
            className={`gcs-chip ${!customMode && amount === d ? 'is-active' : ''}`}
            onClick={() => pickPreset(d)}
          >
            {currency}{d}
          </button>
        ))}
        {customAllowed && (
          <button
            type="button"
            className={`gcs-chip ${customMode ? 'is-active' : ''}`}
            onClick={() => setCustomMode(true)}
          >
            Custom
          </button>
        )}
      </div>

      {customMode && (
        <div className="gcs-custom">
          <div className="gcs-custom-field">
            <span className="gcs-custom-cur">{currency}</span>
            <input
              type="number"
              inputMode="numeric"
              value={customText}
              onChange={(e) => onCustomInput(e.target.value)}
              onBlur={onCustomBlur}
              placeholder="Enter amount"
              min={min || undefined}
              max={max || undefined}
            />
          </div>
          <p className="gcs-custom-hint">
            {max ? `Between ${currency}${min} and ${currency}${max}` : `Minimum ${currency}${min}`}
          </p>
        </div>
      )}

      {hasPromo && (
        <div className="gcs-promo" role="note">
          <div className="gcs-promo-main">
            <span className="gcs-promo-pay">Pay {currency}{pay}</span>
            <span className="gcs-promo-spend">{currency}{amount} to spend</span>
          </div>
          <span className="gcs-promo-badge">
            <SparkIcon />
            Save {currency}{amount - pay}
          </span>
        </div>
      )}
    </section>
  );
};

export default StepAmount;
